import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';

const RANK_STYLES = {
  1: { bg: 'bg-gradient-to-r from-yellow-50 to-amber-50', border: 'border-yellow-300', icon: 'fa-crown', iconColor: 'text-yellow-500', medal: 'bg-gradient-to-br from-yellow-400 to-amber-500' },
  2: { bg: 'bg-gradient-to-r from-gray-50 to-slate-50', border: 'border-gray-300', icon: 'fa-medal', iconColor: 'text-gray-400', medal: 'bg-gradient-to-br from-gray-300 to-gray-400' },
  3: { bg: 'bg-gradient-to-r from-orange-50 to-amber-50', border: 'border-orange-300', icon: 'fa-medal', iconColor: 'text-orange-400', medal: 'bg-gradient-to-br from-orange-300 to-orange-500' },
};

const LEVEL_TITLES = ['Rookie', 'Scout', 'Agent', 'Specialist', 'Expert', 'Senior', 'Lead', 'Principal', 'Director', 'VP', 'SVP', 'Chief', 'Master', 'Grand Master', 'Legend', 'Mythic', 'Immortal', 'Transcendent', 'Omega', 'Solar God'];

function XpBar({ xp, level }) {
  const xpForNext = level * 200;
  const xpInLevel = xp - ((level - 1) * 200);
  const pct = Math.min(100, (xpInLevel / 200) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-surface-alt rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-text-muted tabular-nums">{xp} XP</span>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    api.get('/api/leaderboard').then(res => {
      setBoard(res.data.leaderboard || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const topThree = board.slice(0, 3);
  const rest = board.slice(3);
  const myEntry = board.find(e => e.email === user?.email);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaderboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight flex items-center gap-3">
          <i className="fa-solid fa-ranking-star text-primary" />Team Leaderboard
        </h1>
        <p className="text-text-muted text-sm mt-1">Track team performance, earn XP, and unlock badges</p>
      </div>

      {/* My Stats Banner */}
      {myEntry && (
        <div className="card-interactive p-5 bg-gradient-to-r from-primary/5 to-emerald-50 border-primary/20" data-testid="my-stats-banner">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {myEntry.name?.charAt(0)}
                </div>
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center text-xs font-bold text-primary border border-primary/20">#{myEntry.rank}</span>
              </div>
              <div>
                <p className="font-heading text-lg font-bold text-text-primary">Your Rank: #{myEntry.rank}</p>
                <p className="text-xs text-text-muted">Level {myEntry.level} - {LEVEL_TITLES[Math.min(myEntry.level - 1, LEVEL_TITLES.length - 1)]}</p>
                <div className="mt-1 w-48"><XpBar xp={myEntry.xp} level={myEntry.level} /></div>
              </div>
            </div>
            <div className="flex gap-4">
              {[
                { label: 'Won', value: myEntry.leads_won, color: 'text-primary' },
                { label: 'Pipeline', value: `$${(myEntry.total_value / 1000).toFixed(0)}K`, color: 'text-accent' },
                { label: 'Conv.', value: `${myEntry.conversion_rate}%`, color: 'text-secondary' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className={`font-heading text-xl font-bold ${s.color} tabular-nums`}>{s.value}</p>
                  <p className="text-[10px] text-text-muted uppercase">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5">
              {myEntry.badges.map((b, i) => (
                <div key={i} className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: b.color + '20' }} title={b.name}>
                  <i className={`fa-solid ${b.icon} text-sm`} style={{ color: b.color }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top 3 Podium */}
      <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
        {[1, 0, 2].map(idx => {
          const entry = topThree[idx];
          if (!entry) return <div key={idx} />;
          const rs = RANK_STYLES[entry.rank] || {};
          const isCenter = idx === 0;
          return (
            <div key={entry.email} className={`${isCenter ? 'order-1 sm:-mt-4' : idx === 0 ? 'order-0' : 'order-2'} ${rs.bg} rounded-2xl border ${rs.border} p-5 text-center animate-slide-up transition-all hover:shadow-lg cursor-pointer`} style={{ animationDelay: `${idx * 100}ms` }} onClick={() => setSelectedUser(entry)} data-testid={`podium-${entry.rank}`}>
              <div className="relative inline-block mb-3">
                <div className={`w-16 h-16 rounded-full ${rs.medal} flex items-center justify-center text-white text-2xl font-bold shadow-lg mx-auto`}>
                  {entry.name?.charAt(0)}
                </div>
                <span className="absolute -top-2 -right-2"><i className={`fa-solid ${rs.icon} ${rs.iconColor} text-xl drop-shadow-sm`} /></span>
              </div>
              <p className="font-heading font-bold text-text-primary text-sm truncate">{entry.name}</p>
              <p className="text-[10px] text-text-muted capitalize">{entry.role}</p>
              <p className="font-heading text-xl font-bold text-primary mt-2 tabular-nums">{entry.xp} XP</p>
              <p className="text-[10px] text-text-muted">Lv.{entry.level} {LEVEL_TITLES[Math.min(entry.level - 1, LEVEL_TITLES.length - 1)]}</p>
              <div className="flex justify-center gap-1 mt-2">
                {entry.badges.slice(0, 3).map((b, i) => (
                  <span key={i} className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: b.color + '20' }} title={b.name}>
                    <i className={`fa-solid ${b.icon} text-[10px]`} style={{ color: b.color }} />
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Full Rankings Table */}
      <div className="card-interactive overflow-hidden">
        <table className="w-full" data-testid="leaderboard-table">
          <thead>
            <tr className="border-b border-border">
              {['Rank', 'Team Member', 'Level', 'Leads Won', 'Pipeline', 'Conv. Rate', 'XP', 'Badges'].map(h => (
                <th key={h} className="px-4 py-3.5 text-left text-[10px] font-bold uppercase tracking-widest text-text-muted bg-surface-alt">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {board.map((entry, i) => {
              const isMe = entry.email === user?.email;
              return (
                <tr key={entry.email} className={`border-b border-border/50 transition-colors cursor-pointer ${isMe ? 'bg-primary/5' : 'hover:bg-surface-alt/50'}`} onClick={() => setSelectedUser(entry)} data-testid={`rank-row-${entry.rank}`}>
                  <td className="px-4 py-3">
                    <span className={`w-8 h-8 rounded-lg inline-flex items-center justify-center text-xs font-bold ${entry.rank <= 3 ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted'}`}>
                      {entry.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xs font-bold">{entry.name?.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">{entry.name} {isMe && <span className="text-xs text-primary">(You)</span>}</p>
                        <p className="text-[11px] text-text-muted capitalize">{entry.role}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-primary">Lv.{entry.level}</span>
                      <span className="text-[10px] text-text-muted truncate max-w-[80px]">{LEVEL_TITLES[Math.min(entry.level - 1, LEVEL_TITLES.length - 1)]}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-bold text-text-primary tabular-nums">{entry.leads_won}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-text-primary">${(entry.total_value / 1000).toFixed(0)}K</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${entry.conversion_rate >= 25 ? 'bg-primary/10 text-primary' : entry.conversion_rate >= 10 ? 'bg-yellow-50 text-yellow-700' : 'text-text-muted'}`}>
                      {entry.conversion_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 font-heading text-sm font-bold text-primary tabular-nums">{entry.xp}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {entry.badges.slice(0, 4).map((b, j) => (
                        <span key={j} className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: b.color + '20' }} title={b.name}>
                          <i className={`fa-solid ${b.icon} text-[10px]`} style={{ color: b.color }} />
                        </span>
                      ))}
                      {entry.badges.length > 4 && <span className="text-[10px] text-text-muted">+{entry.badges.length - 4}</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-border max-w-lg w-full mx-4 p-6 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-text-primary">Performance Detail</h3>
              <button onClick={() => setSelectedUser(null)} className="p-2 rounded-lg hover:bg-surface-alt transition-colors" data-testid="close-detail-modal"><i className="fa-solid fa-xmark text-text-muted" /></button>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {selectedUser.name?.charAt(0)}
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-text-primary">{selectedUser.name}</p>
                <p className="text-xs text-text-muted capitalize">{selectedUser.role} | Rank #{selectedUser.rank}</p>
                <p className="text-xs text-primary font-semibold mt-0.5">Level {selectedUser.level} - {LEVEL_TITLES[Math.min(selectedUser.level - 1, LEVEL_TITLES.length - 1)]}</p>
                <div className="mt-1 w-48"><XpBar xp={selectedUser.xp} level={selectedUser.level} /></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: 'Leads Created', value: selectedUser.leads_created, icon: 'fa-users' },
                { label: 'Leads Won', value: selectedUser.leads_won, icon: 'fa-trophy' },
                { label: 'Qualified', value: selectedUser.leads_qualified, icon: 'fa-star' },
                { label: 'Pipeline Value', value: `$${(selectedUser.total_value / 1000).toFixed(0)}K`, icon: 'fa-dollar-sign' },
                { label: 'Proposals', value: selectedUser.proposals, icon: 'fa-file-invoice' },
                { label: 'Conv. Rate', value: `${selectedUser.conversion_rate}%`, icon: 'fa-bullseye' },
              ].map(s => (
                <div key={s.label} className="p-3 rounded-xl bg-surface-alt text-center">
                  <i className={`fa-solid ${s.icon} text-primary text-xs mb-1 block`} />
                  <p className="text-lg font-bold text-text-primary">{s.value}</p>
                  <p className="text-[10px] text-text-muted">{s.label}</p>
                </div>
              ))}
            </div>

            {selectedUser.badges.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Badges Earned</p>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.badges.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: b.color + '15' }}>
                      <i className={`fa-solid ${b.icon} text-sm`} style={{ color: b.color }} />
                      <span className="text-xs font-semibold" style={{ color: b.color }}>{b.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
